import { CONFIG } from '../config';
import { EventBus } from '../core/EventBus';
import { clamp, formatMoney, round2 } from '../core/math';
import { decorDef, decorPoints } from '../content/decor';
import { interiorPoints } from '../content/interior';
import { ITEMS } from '../content/items';
import {
  absoluteMinutes,
  cloutMultiplier,
  dayOfWeek,
  decayStats,
  rentDueDay,
  rentForWeek,
  weekOf,
  type DecayOptions,
} from './rules';
import type {
  CounterId,
  FeedPost,
  GameState,
  InteriorState,
  Inventory,
  ItemId,
  StatId,
  Stats,
} from './types';
import type { IconToken } from '../icons/index';

export type ToastKind = 'info' | 'good' | 'bad' | 'meme';

export interface Toast {
  text: string;
  kind?: ToastKind;
  icon?: IconToken;
}

export type PostInput = Omit<FeedPost, 'id' | 'day' | 'minutes' | 'likes'> & { likes?: number };

/** A bundle of state changes, used by events, dialogue choices and jobs. */
export interface Outcome {
  text?: string;
  money?: number;
  clout?: number;
  stats?: Partial<Stats>;
  items?: Partial<Inventory>;
  minutes?: number;
  decor?: string;
  achievement?: string;
  post?: PostInput;
  headline?: string;
}

export interface StoreEvents {
  change: undefined;
  money: number;
  item: { id: ItemId; delta: number };
  achievement: string;
  decor: readonly string[];
  interior: InteriorState;
  toast: Toast;
  float: { text: string; color?: string };
  post: FeedPost;
  headline: string;
}

export const STAT_ICONS: Record<StatId, string> = {
  health: ':heart:',
  hunger: ':hotdog:',
  energy: ':bolt:',
  buzz: ':beer:',
};

const MAX_FEED = 40;

/**
 * Owns the GameState and is the only thing allowed to mutate it.
 * Everything else reads `state` and listens on `bus`.
 */
export class Store {
  readonly bus = new EventBus<StoreEvents>();

  constructor(public state: GameState) {}

  replace(state: GameState): void {
    this.state = state;
    this.changed();
  }

  changed(): void {
    this.bus.emit('change', undefined);
  }

  // --- money ---------------------------------------------------------------

  get money(): number {
    return this.state.money;
  }

  canAfford(amount: number): boolean {
    return this.state.money + 1e-9 >= amount;
  }

  earn(amount: number): void {
    if (amount === 0) return;
    this.state.money = round2(this.state.money + amount);
    this.bus.emit('money', amount);
    this.changed();
  }

  /** Returns false (and changes nothing) if you can't afford it. */
  spend(amount: number): boolean {
    if (!this.canAfford(amount)) return false;
    this.state.money = round2(this.state.money - amount);
    this.bus.emit('money', -amount);
    this.changed();
    return true;
  }

  addDebt(amount: number): void {
    this.state.debt = round2(Math.max(0, this.state.debt + amount));
    this.changed();
  }

  // --- inventory -----------------------------------------------------------

  count(id: ItemId): number {
    return this.state.inv[id];
  }

  has(id: ItemId, n = 1): boolean {
    return this.state.inv[id] >= n;
  }

  give(id: ItemId, n: number): void {
    if (n === 0) return;
    const before = this.state.inv[id];
    this.state.inv[id] = Math.max(0, before + n);
    this.bus.emit('item', { id, delta: this.state.inv[id] - before });
    this.changed();
  }

  take(id: ItemId, n = 1): boolean {
    if (!this.has(id, n)) return false;
    this.give(id, -n);
    return true;
  }

  // --- stats ---------------------------------------------------------------

  stat(id: StatId): number {
    return this.state.stats[id];
  }

  addStat(id: StatId, delta: number): void {
    this.state.stats[id] = clamp(this.state.stats[id] + delta, 0, 100);
  }

  /** Adds clout, applying the liquid-courage multiplier to gains. Returns the amount applied. */
  addClout(delta: number): number {
    // Fresh clothes from the laundromat: people just treat you better all day.
    const fresh = delta > 0 && this.flag('fresh') === this.day ? 1 : 0;
    const applied =
      delta > 0 ? Math.round(delta * cloutMultiplier(this.state.stats.buzz)) + fresh : Math.round(delta);
    this.state.clout = Math.max(0, this.state.clout + applied);
    this.changed();
    return applied;
  }

  rating(): number {
    const s = this.state;
    return (
      s.clout +
      decorPoints(s.decor) +
      interiorPoints(this.displayedInterior()) +
      s.achievements.length * 2 +
      (s.camaro >= 100 ? 15 : 0)
    );
  }

  // --- counters, flags, achievements ---------------------------------------

  incr(id: CounterId, n = 1): void {
    this.state.counters[id] = (this.state.counters[id] ?? 0) + n;
    this.changed();
  }

  counter(id: CounterId): number {
    return this.state.counters[id] ?? 0;
  }

  flag(key: string): number | boolean | string | undefined {
    return this.state.flags[key];
  }

  setFlag(key: string, value: number | boolean | string): void {
    this.state.flags[key] = value;
    this.changed();
  }

  doneToday(key: string): boolean {
    return this.state.flags[`day_${key}`] === this.state.time.day;
  }

  markToday(key: string): void {
    this.state.flags[`day_${key}`] = this.state.time.day;
  }

  unlock(id: string): boolean {
    if (this.state.achievements.includes(id)) return false;
    this.state.achievements.push(id);
    this.bus.emit('achievement', id);
    this.changed();
    return true;
  }

  hasDecor(id: string): boolean {
    return this.state.decor.includes(id);
  }

  addDecor(id: string): void {
    if (this.hasDecor(id)) return;
    this.state.decor.push(id);
    this.bus.emit('decor', this.state.decor);
    this.changed();
  }

  // --- inside the trailer ------------------------------------------------------

  hasInterior(id: string): boolean {
    return this.state.interior.items.includes(id);
  }

  /** Owned and not put away. */
  displayedInterior(): string[] {
    const { items, stored } = this.state.interior;
    return items.filter((id) => !stored.includes(id));
  }

  addInterior(id: string): void {
    if (this.hasInterior(id)) return;
    this.state.interior.items.push(id);
    this.interiorChanged();
  }

  /** Put something in the closet, or take it back out. */
  setStored(id: string, stored: boolean): void {
    const i = this.state.interior;
    i.stored = i.stored.filter((x) => x !== id);
    if (stored && i.items.includes(id)) i.stored.push(id);
    this.interiorChanged();
  }

  setFinish(which: 'walls' | 'floor', id: string): void {
    this.state.interior[which] = id;
    this.interiorChanged();
  }

  private interiorChanged(): void {
    this.bus.emit('interior', this.state.interior);
    this.changed();
  }

  // --- social ---------------------------------------------------------------

  post(input: PostInput): FeedPost {
    const post: FeedPost = {
      ...input,
      likes: input.likes ?? 0,
      id: this.state.nextPostId++,
      day: this.state.time.day,
      minutes: Math.floor(this.state.time.minutes),
    };
    this.state.feed.unshift(post);
    if (this.state.feed.length > MAX_FEED) this.state.feed.length = MAX_FEED;
    this.bus.emit('post', post);
    return post;
  }

  toast(text: string, kind: ToastKind = 'info', icon?: IconToken): void {
    this.bus.emit('toast', { text, kind, icon });
  }

  float(text: string, color?: string): void {
    this.bus.emit('float', { text, color });
  }

  headline(text: string): void {
    this.bus.emit('headline', text);
  }

  // --- time ------------------------------------------------------------------

  get day(): number {
    return this.state.time.day;
  }

  get minutes(): number {
    return this.state.time.minutes;
  }

  get week(): number {
    return weekOf(this.state.time.day);
  }

  get dayOfWeek(): number {
    return dayOfWeek(this.state.time.day);
  }

  get now(): number {
    return absoluteMinutes(this.state.time.day, this.state.time.minutes);
  }

  /** Advances the clock and decays needs. Returns how many midnights were crossed. */
  advanceTime(minutes: number, opts: DecayOptions = {}): number {
    const s = this.state;
    const heatWave = s.flags['heatwave'] === s.time.day;
    s.stats = decayStats(s.stats, minutes, { heatWave, ...opts });
    s.time.minutes += minutes;
    let newDays = 0;
    while (s.time.minutes >= 1440) {
      s.time.minutes -= 1440;
      s.time.day += 1;
      newDays++;
    }
    return newDays;
  }

  rentStatus(): { week: number; amount: number; dueDay: number; daysUntil: number; paid: boolean } {
    const week = this.week;
    const dueDay = rentDueDay(week);
    return {
      week,
      // Once the residents own the park, rent is just co-op dues.
      amount: this.state.flags.park_owned ? CONFIG.coopDues : rentForWeek(week),
      dueDay,
      daysUntil: dueDay - this.state.time.day,
      paid: this.state.rent.paidThroughWeek >= week,
    };
  }

  /** Pays this week's rent. Returns false if already paid or unaffordable. */
  payRent(): boolean {
    const r = this.rentStatus();
    if (r.paid || !this.spend(r.amount)) return false;
    this.state.rent.paidThroughWeek = r.week;
    this.incr('rentPaid');
    return true;
  }

  /** Records a missed rent payment. Returns true if that was strike three (evicted). */
  addStrike(): boolean {
    this.state.rent.strikes += 1;
    const evicted = isEvicted(this.state.rent.strikes);
    if (evicted) this.state.status = 'evicted';
    this.changed();
    return evicted;
  }

  // --- outcomes -------------------------------------------------------------

  /** Applies an outcome and returns short human-readable chips describing what changed. */
  apply(o: Outcome): string[] {
    const chips: string[] = [];
    if (o.money) {
      if (o.money > 0) this.earn(o.money);
      else {
        const amount = Math.min(-o.money, this.state.money);
        if (amount > 0) this.spend(amount);
      }
      chips.push(`${o.money > 0 ? '+' : '-'}${formatMoney(Math.abs(o.money))}`);
    }
    if (o.clout) {
      const applied = this.addClout(o.clout);
      chips.push(`${applied >= 0 ? '+' : ''}${applied} Clout`);
    }
    if (o.stats) {
      for (const [id, delta] of Object.entries(o.stats) as [StatId, number][]) {
        if (!delta) continue;
        this.addStat(id, delta);
        chips.push(`${delta > 0 ? '+' : ''}${Math.round(delta)} ${STAT_ICONS[id]}`);
      }
    }
    if (o.items) {
      for (const [id, n] of Object.entries(o.items) as [ItemId, number][]) {
        if (!n) continue;
        this.give(id, n);
        chips.push(`${n > 0 ? '+' : ''}${n} ${ITEMS[id].icon}`);
      }
    }
    if (o.minutes) {
      this.advanceTime(o.minutes);
      chips.push(`:stopwatch: ${o.minutes >= 60 ? `${Math.round(o.minutes / 60)}h` : `${o.minutes}m`}`);
    }
    if (o.decor) {
      this.addDecor(o.decor);
      chips.push(`${decorDef(o.decor)?.icon ?? ':trailer:'} New yard decor`);
    }
    if (o.achievement) this.unlock(o.achievement);
    if (o.post) this.post(o.post);
    if (o.headline) this.headline(o.headline);
    this.changed();
    return chips;
  }
}

export function isEvicted(strikes: number): boolean {
  return strikes >= CONFIG.maxStrikes;
}
